import React, { PropsWithChildren, useCallback, useMemo } from 'react'

import { Col, Row } from '@amzn/stencil-react-components/layout'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'

import { IconWithTooltip } from 'src/components/IconWithTooltip'
import { ResponsiveRow } from 'src/components/ResponsiveRow'
import { isDefaultLocale } from 'src/contexts/ModuleLocaleContext'
import { useItemEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import { ItemDTO } from 'src/models/dto/items/ItemDTO'
import { Locale } from 'src/models/dto/Locale'
import {
    ItemEditorCheckboxInput,
    ItemEditorTextInput,
} from 'src/pages/module-builder/item-editors/ItemEditorInputs'

export interface ItemHandler {
    updateLabel: (itemId: string, value: string) => void
    updateOptional?: (itemId: string, value: boolean) => void
    updatePreserveOrder?: (itemId: string, value: boolean) => void
}

// Props interface for the component
export interface ItemEditorBasicInfoProps {
    handler: ItemHandler
    itemId: string
    workflowEntityId: string
    locale: Locale
    editDisabled?: boolean
    showValidationError?: boolean
    disableOptional?: boolean
    disablePreserveOrder?: boolean
}

export const ItemEditorBasicInfo = ({
    handler,
    itemId,
    workflowEntityId,
    locale,
    editDisabled = false,
    showValidationError = false,
    disableOptional = false,
    disablePreserveOrder = false,
    children,
}: PropsWithChildren<ItemEditorBasicInfoProps>) => {
    const { entity: itemDTO } = useItemEntity<ItemDTO>({
        entityId: itemId,
        workflowEntityId,
    })
    const { entity: validationErrorEntity } = useValidationErrorEntity(itemId)
    const { matches } = useBreakpoints()

    const gridGap = matches.s ? 'S100' : matches.m ? 'S200' : 'S300'
    const itemIdFlex = `0 1 ${matches.s ? '100%' : matches.m ? '50%' : '450px'}`
    const optionalFlex = `0 0 ${matches.s ? '100%' : 'auto'}`
    const preserveOrderCheckboxFlex = `0 0 ${matches.s ? '100%' : 'auto'}`
    const padding = useMemo(() => ({ top: `${matches.s ? '8px' : '35px'}` }), [matches.s])

    // Handler methods for updating values with error handling
    const updateLabel = useCallback(
        (nextValue: string) => {
            handler.updateLabel(itemId, nextValue)
        },
        [handler, itemId]
    )

    const updateOptional = useCallback(
        (nextValue: boolean) => {
            /* istanbul ignore else */
            if (handler.updateOptional) {
                handler.updateOptional(itemId, nextValue)
            } else {
                console.error(
                    `ItemEditorBasicInfo: The updateOptional handler for item ${itemId} is missing.`,
                    'Please set disableOptional to be true to remove the checkbox'
                )
            }
        },
        [handler, itemId]
    )

    const updatePreserveOrder = useCallback(
        (nextValue: boolean) => {
            /* istanbul ignore else */
            if (handler.updatePreserveOrder) {
                handler.updatePreserveOrder(itemId, nextValue)
            } else {
                console.error(
                    `ItemEditorBasicInfo: The updatePreserveOrder handler for item ${itemId} is missing.`,
                    'Please set disablePreserveOrder to be true to remove the checkbox'
                )
            }
        },
        [handler, itemId]
    )

    return (
        <ResponsiveRow gridGap={gridGap} alignItems={'flex-start'} flexWrap={'wrap'}>
            <Col flex={itemIdFlex} width='100%'>
                <ItemEditorTextInput
                    dataTestId={'unique-item-id'}
                    inputId={'unique-item-id'}
                    disabled={!isDefaultLocale(locale) || editDisabled}
                    value={itemDTO.label}
                    placeholder={'Some human readable label'}
                    itemId={itemDTO.id}
                    setValue={updateLabel}
                    validationErrorMessage={(
                        validationErrorEntity.validationErrors.label ?? []
                    ).join(', ')}
                    showError={showValidationError}
                    labelText={'Unique Item ID'}
                />
            </Col>
            {!disableOptional && (
                <Col gridGap={'S200'} padding={padding} flex={optionalFlex}>
                    <ItemEditorCheckboxInput
                        dataTestId={'optional-checkbox'}
                        disabled={!isDefaultLocale(locale) || editDisabled}
                        inputId={'optional'}
                        itemId={itemDTO.id}
                        labelText={'Optional'}
                        value={itemDTO.optional}
                        setValue={updateOptional}
                    />
                </Col>
            )}
            {!disablePreserveOrder && (
                <Col gridGap={'S200'} padding={padding} flex={preserveOrderCheckboxFlex}>
                    <Row alignItems={'center'}>
                        <ItemEditorCheckboxInput
                            dataTestId='preserve-order-checkbox'
                            inputId={'preserve-order'}
                            itemId={itemDTO.id}
                            labelText={'Preserve order'}
                            disabled={!isDefaultLocale(locale) || editDisabled}
                            value={itemDTO.preserveOrder}
                            setValue={updatePreserveOrder}
                        />
                        <IconWithTooltip tooltipText='When randomizing page, ensure this items position is never changed' />
                    </Row>
                </Col>
            )}
            {children}
        </ResponsiveRow>
    )
}
